import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, Smartphone, Share, Plus, Check, ChevronRight, Users, MapPin, Star } from 'lucide-react';

export default function DownloadPage() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Detect device
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroidDevice = /Android/.test(ua);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    { icon: Users, text: "Find riding partners nearby" },
    { icon: MapPin, text: "Join local rides & clubs" },
    { icon: Star, text: "Build your cycling reputation" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-gray-900 to-black">
      {/* Header */}
      <div className="pt-12 pb-8 px-6 text-center">
        <div className="w-24 h-24 bg-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/30">
          <span className="text-5xl">🚴</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-2">RydrLink</h1>
        <p className="text-purple-300 text-lg">Meet. Match. Ride.</p>
      </div>

      {/* Main Content */}
      <div className="px-6 pb-12">
        {isInstalled ? (
          /* Already Installed */
          <Card className="bg-green-900/50 border-green-500/30 mb-6">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">App Installed!</h2>
              <p className="text-green-300 mb-4">RydrLink is ready on your home screen</p>
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/')}
              >
                Open App
              </Button>
            </CardContent>
          </Card>
        ) : isIOS ? (
          /* iOS Instructions */
          <Card className="bg-gray-800/50 border-purple-500/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Install on iPhone</h2>
                  <p className="text-purple-300 text-sm">Add to your home screen</p>
                </div>
              </div>

              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700 mb-4"
                onClick={() => setShowIOSSteps(!showIOSSteps)}
              >
                <Share className="w-5 h-5 mr-2" />
                Show Me How
                <ChevronRight className={`w-5 h-5 ml-2 transition-transform ${showIOSSteps ? 'rotate-90' : ''}`} />
              </Button>

              {showIOSSteps && (
                <div className="space-y-4 mt-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-900/50 rounded-lg">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                    <div>
                      <p className="text-white font-medium">Tap the Share button</p>
                      <p className="text-gray-400 text-sm">At the bottom of Safari (box with arrow pointing up)</p>
                      <div className="mt-2 p-2 bg-gray-800 rounded inline-flex items-center gap-2">
                        <Share className="w-5 h-5 text-blue-400" />
                        <span className="text-gray-300 text-sm">Share</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-900/50 rounded-lg">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                    <div>
                      <p className="text-white font-medium">Scroll down and tap</p>
                      <div className="mt-2 p-2 bg-gray-800 rounded inline-flex items-center gap-2">
                        <Plus className="w-5 h-5 text-gray-300" />
                        <span className="text-gray-300 text-sm">Add to Home Screen</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-900/50 rounded-lg">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                    <div>
                      <p className="text-white font-medium">Tap "Add"</p>
                      <p className="text-gray-400 text-sm">RydrLink will appear on your home screen!</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : isAndroid && deferredPrompt ? (
          /* Android with Install Prompt */
          <Card className="bg-gray-800/50 border-purple-500/30 mb-6">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Get the App</h2>
              <p className="text-gray-400 mb-6">Install RydrLink for the best experience</p>
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6"
                onClick={handleInstall}
              >
                <Download className="w-5 h-5 mr-2" />
                Install RydrLink
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Desktop or fallback */
          <Card className="bg-gray-800/50 border-purple-500/30 mb-6">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Open on Your Phone</h2>
              <p className="text-gray-400 mb-6">Scan this page's QR code or visit on mobile to install</p>
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => navigate('/auth')}
              >
                Continue in Browser
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Features */}
        <div className="space-y-3 mb-8">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4 bg-gray-800/30 rounded-xl">
              <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                <feature.icon className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-white">{feature.text}</p>
            </div>
          ))}
        </div>

        {/* Already have account */}
        {!isInstalled && (
          <div className="text-center">
            <p className="text-gray-500 mb-2">Already have an account?</p>
            <Button 
              variant="ghost" 
              className="text-purple-400 hover:text-purple-300"
              onClick={() => navigate('/auth')}
            >
              Sign in here
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center pb-8 px-6">
        <p className="text-gray-600 text-sm">
          Free to use • No app store needed
        </p>
      </div>
    </div>
  );
}
